/**
 * Image Bounds Overlay Extension for Map Facade
 *
 * This module extends the map facade with support for rendering georeferenced image overlays
 * on Google Maps. Each overlay is bounded by geographic coordinates and can be rotated.
 *
 * Architecture:
 * - Uses Google Maps OverlayView API for precise positioning within the map projection
 * - Lazy-loads the OverlayView subclass once Maps API is available (singleton pattern)
 * - Delegates all facade interaction through getGoogleMaps() and getMapInstance()
 * - Returns opaque overlay handles (attach, detach, syncData) to decouple plugin from Maps API
 * - Two-element DOM per overlay: masked SVG image in overlayLayer, SVG hit layer in overlayMouseTarget
 *
 * Performance:
 * - Caches computed pixel rectangles to avoid redundant DOM writes on every draw() call
 * - CSS transforms used for rotation (hardware-accelerated)
 * - Native browser hit-testing via SVG clip-path — no JavaScript geometry math per event
 * - Early exit optimization when projection hasn't changed
 *
 * @module mapLibrary/overlays
 */

import { applyPositionStyle, buildHitSvgLayer, buildImageBoundsSvg, getOrCreateCommunityWrapper } from './overlayDom';
import { computePixelBounds, hasPixelRectChanged, isDragGesture } from './overlayUtils';

/**
 * Creates an image-bounds overlay backed by a google.maps.OverlayView instance.
 * Uses composition: lifecycle methods are assigned as properties rather than
 * inherited via a subclass. All mutable state lives in closure variables.
 *
 * @param {object} sitePlan   - Initial site plan data
 * @param {object} handlers   - { onSitePlanClick, onSitePlanHover, onSitePlanLeave, classNames }
 * @param {object} google     - Google Maps API namespace
 * @returns {{ overlayView: google.maps.OverlayView, syncData: Function }}
 */
function buildImageBoundsOverlay(sitePlan, handlers, google) {
  let currentSitePlan = sitePlan;
  let containerElement = null;
  let communityWrapperElement = null;
  let imageElement = null;
  let lastPixelRect = null;
  let svgHitElement = null;
  let hitRectElement = null;
  let pointerDownPos = null;
  let boundHandleClick = null;
  let boundHandleMouseDown = null;
  let boundHandleMouseEnter = null;
  let boundHandleMouseLeave = null;

  const onSitePlanClick = handlers?.onSitePlanClick ?? null;
  const onSitePlanHover = handlers?.onSitePlanHover ?? null;
  const onSitePlanLeave = handlers?.onSitePlanLeave ?? null;
  const classNames = handlers?.classNames ?? {};

  /*
   * Builds a transparent SVG element clipped to the site plan's SvgMask.
   * The SVG viewBox matches ViewBoxWidth × ViewBoxHeight so the mask path
   * coordinates align with the rendered overlay bounds at any zoom level.
   *
   * When SvgMask is present: only pixels inside the mask shape are hittable.
   * When SvgMask is absent: the full bounding box is hittable.
   */
  function buildSvgHitLayer() {
    const { svgMask, viewBoxWidth, viewBoxHeight, sitePlanId } = currentSitePlan;
    const clipPathId = `sp-mask-${sitePlanId}`;

    const { svgElement, hitRect: hitAreaRect } = buildHitSvgLayer(
      viewBoxWidth, viewBoxHeight, clipPathId, svgMask, classNames.mask
    );

    if (onSitePlanClick) {
      boundHandleMouseDown = (event) => {
        pointerDownPos = { x: event.clientX, y: event.clientY };
      };
      hitAreaRect.addEventListener('mousedown', boundHandleMouseDown);

      boundHandleClick = (event) => {
        if (isDragGesture(pointerDownPos, event.clientX, event.clientY)) {
          return;
        }
        onSitePlanClick(currentSitePlan);
      };
      hitAreaRect.addEventListener('click', boundHandleClick);
    }

    if (onSitePlanHover) {
      boundHandleMouseEnter = () => onSitePlanHover(currentSitePlan);
      hitAreaRect.addEventListener('mouseenter', boundHandleMouseEnter);
    }

    boundHandleMouseLeave = () => {
      if (onSitePlanLeave) {
        onSitePlanLeave(currentSitePlan);
      }
    };
    hitAreaRect.addEventListener('mouseleave', boundHandleMouseLeave);

    hitRectElement = hitAreaRect;
    return svgElement;
  }

  const overlayView = new google.maps.OverlayView();

  overlayView.onAdd = function onAdd() {
    const panes = overlayView.getPanes();

    // Image layer — SVG with site plan image clipped to SvgMask boundary; sits in overlayLayer pane
    const div = document.createElement('div');
    if (classNames.container) {
      div.classList.add(classNames.container);
    }

    const { svgElement: imageSvgElement, imageEl } = buildImageBoundsSvg(
      currentSitePlan.viewBoxWidth,
      currentSitePlan.viewBoxHeight,
      currentSitePlan.sitePlanId,
      currentSitePlan.svgMask,
      currentSitePlan.mapImageUrl,
      classNames.image
    );

    div.appendChild(imageSvgElement);
    containerElement = div;
    imageElement = imageEl;

    // Community wrapper: one element per communityId shared across all site plans of that community.
    // The active class is applied to this wrapper, not to individual containers.
    const communityId = String(currentSitePlan.communityId);
    communityWrapperElement = getOrCreateCommunityWrapper(
      panes.overlayLayer,
      'sitePlanCommunityId',
      communityId
    );

    communityWrapperElement.appendChild(containerElement);

    // SVG hit layer — transparent shape clipped to SvgMask; sits in overlayMouseTarget pane
    // This gives the browser native hit-testing within the irregular community boundary
    svgHitElement = buildSvgHitLayer();
    panes.overlayMouseTarget.appendChild(svgHitElement);
  };

  overlayView.draw = function draw() {
    if (!containerElement) {
      return;
    }

    const projection = overlayView.getProjection();
    if (!projection) {
      return;
    }

    const { topRightLatitude, topRightLongitude, bottomLeftLatitude, bottomLeftLongitude, rotation } = currentSitePlan;

    const swLatLng = new google.maps.LatLng(bottomLeftLatitude, bottomLeftLongitude);
    const southWest = projection.fromLatLngToDivPixel(swLatLng);
    const neLatLng = new google.maps.LatLng(topRightLatitude, topRightLongitude);
    const northEast = projection.fromLatLngToDivPixel(neLatLng);

    if (!southWest || !northEast) {
      return;
    }

    const pixelRect = computePixelBounds(southWest, northEast, rotation);
    if (!hasPixelRectChanged(lastPixelRect, pixelRect)) {
      return;
    }

    lastPixelRect = pixelRect;
    const { left, top, width, height, transform } = pixelRect;
    applyPositionStyle(containerElement, left, top, width, height, transform);
    if (svgHitElement) {
      applyPositionStyle(svgHitElement, left, top, width, height, transform);
    }
  };

  overlayView.onRemove = function onRemove() {
    if (hitRectElement) {
      if (boundHandleMouseDown) {
        hitRectElement.removeEventListener('mousedown', boundHandleMouseDown);
      }
      if (boundHandleClick) {
        hitRectElement.removeEventListener('click', boundHandleClick);
      }
      if (boundHandleMouseEnter) {
        hitRectElement.removeEventListener('mouseenter', boundHandleMouseEnter);
      }
      if (boundHandleMouseLeave) {
        hitRectElement.removeEventListener('mouseleave', boundHandleMouseLeave);
      }
    }

    if (svgHitElement && svgHitElement.parentNode) {
      svgHitElement.parentNode.removeChild(svgHitElement);
    }

    if (containerElement && containerElement.parentNode) {
      containerElement.parentNode.removeChild(containerElement);
    }

    // Remove the community wrapper only when this was its last child.
    if (communityWrapperElement && communityWrapperElement.childElementCount === 0) {
      communityWrapperElement.parentNode?.removeChild(communityWrapperElement);
    }

    containerElement = null;
    communityWrapperElement = null;
    imageElement = null;
    lastPixelRect = null;
    boundHandleClick = null;
    boundHandleMouseDown = null;
    boundHandleMouseEnter = null;
    boundHandleMouseLeave = null;
    svgHitElement = null;
    hitRectElement = null;
    pointerDownPos = null;
  };

  function syncData(sp) {
    if (imageElement && sp.mapImageUrl !== currentSitePlan.mapImageUrl) {
      imageElement.setAttribute('href', sp.mapImageUrl);
    }

    const positionChanged =
      sp.topRightLatitude !== currentSitePlan.topRightLatitude ||
      sp.topRightLongitude !== currentSitePlan.topRightLongitude ||
      sp.bottomLeftLatitude !== currentSitePlan.bottomLeftLatitude ||
      sp.bottomLeftLongitude !== currentSitePlan.bottomLeftLongitude ||
      sp.rotation !== currentSitePlan.rotation;

    currentSitePlan = sp;
    if (positionChanged) {
      overlayView.draw();
    }
  }

  return { overlayView, syncData };
}

/**
 * Extends the map facade with image-bounds overlay support.
 *
 * Exposes:
 *   createImageBoundsOverlay(sitePlan, handlers) → { attach, detach, syncData }
 *
 * handlers:
 *   onSitePlanClick(sitePlan)  — fired on click; enables pointer-events on the overlay
 *   onSitePlanHover(sitePlan)  — fired on mouseenter; enables pointer-events on the overlay
 */
export default function overlays(facade, adapterState) {
  function createImageBoundsOverlay(sitePlan, handlers) {
    const google = adapterState.google;
    const mapInstance = adapterState.map;

    if (!google || !mapInstance) {
      return null;
    }

    const { overlayView, syncData } = buildImageBoundsOverlay(sitePlan, handlers, google);

    return {
      attach() {
        overlayView.setMap(mapInstance);
      },
      detach() {
        overlayView.setMap(null);
      },
      syncData(sp) {
        syncData(sp);
      },
    };
  }

  return {
    ...facade,
    createImageBoundsOverlay,
  };
}
